import React, { useState, useEffect, useRef } from 'react';
import { FilterSpecification } from 'maplibre-gl';
import styles from './TimeWidgetContainer.module.css';
import { ClockFading } from 'lucide-react';
import { SelectInput, SelectOption } from '../../../common/inputs/SelectInput/SelectInput';
import useGeoData from '../../../store/useGeoData';
import { useShallow } from 'zustand/react/shallow';
import Slider from 'rc-slider'; // Corrected import to default
import 'rc-slider/assets/index.css';

interface TimeWidgetContainerProps {
  isVisible: boolean;
}

import { LayerBase, GeoDataState } from '../../../store/types';

// Rimuovi React.FC per risolvere il problema di tipo e l'errore di default export
const TimeWidgetContainer = ({ isVisible }: TimeWidgetContainerProps) => {
  const MIN_SLIDER_VALUE = 0;
  const MAX_SLIDER_VALUE = 100; // Usato come fallback se non ci sono uniqueTimestampsForSlider

  const [selectedLayerId, setSelectedLayerId] = useState<string | number>('');
  const [selectedField, setSelectedField] = useState<string | number>('');
  const [sliderValues, setSliderValues] = useState<[number, number]>([MIN_SLIDER_VALUE, MAX_SLIDER_VALUE]);
  const [fieldOptions, setFieldOptions] = useState<SelectOption[]>([]);
  const [_featureCount, setFeatureCount] = useState<number | null>(null);
  const [_sliderUpdateMessage, setSliderUpdateMessage] = useState<string>('');
  const [uniqueTimestampsForSlider, setUniqueTimestampsForSlider] = useState<number[]>([]);
  const [featureIdAndTimestampList, setFeatureIdAndTimestampList] = useState<{ idValue: string | number, timestamp: number }[]>([]);
  const [_featuresInRangeExceptionCount, setFeaturesInRangeExceptionCount] = useState<number | null>(null);
  const [actualIdFieldUsedForFilter, setActualIdFieldUsedForFilter] = useState<string | null>(null); // MODIFICATO
  const [lastAppliedFilterString, setLastAppliedFilterString] = useState<string | null>(null);
  const [_isExpanded, setIsExpanded] = useState(false);
  const { dataLayers, mapInstance, updateLayerProperties } = useGeoData(
    useShallow((state: GeoDataState) => ({
      dataLayers: state.dataLayers,
      mapInstance: state.mapInstance,
      updateLayerProperties: state.updateLayerProperties,
    }))
  );

  const prevSelectedLayerIdRef = useRef<string | number | null>(null);
  const prevSelectedFieldRef = useRef<string | number | null>(null);

  // Funzione per formattare i timestamp (riutilizzata)
  const formatTimestampForDisplay = (timestamp: number): string => {
    const date = new Date(timestamp);
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // I mesi sono 0-indicizzati
    const year = date.getUTCFullYear();
    if (date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0 || date.getUTCSeconds() !== 0) {
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    return `${day}/${month}/${year}`;
  };

  const [currentMinSliderValueText, setCurrentMinSliderValueText] = useState('');
  const [currentMaxSliderValueText, setCurrentMaxSliderValueText] = useState('');
  const [minLabelStyle, setMinLabelStyle] = useState<React.CSSProperties>({});
  const [maxLabelStyle, setMaxLabelStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (uniqueTimestampsForSlider.length > 0 && sliderValues.length === 2) {
      const minIndex = sliderValues[0];
      const maxIndex = sliderValues[1];
      const totalUniqueTimestamps = uniqueTimestampsForSlider.length;

      // Gestione etichetta Min
      if (minIndex >= 0 && minIndex < totalUniqueTimestamps) {
        setCurrentMinSliderValueText(formatTimestampForDisplay(uniqueTimestampsForSlider[minIndex]));
        if (totalUniqueTimestamps > 1) {
          const minPercent = (minIndex / (totalUniqueTimestamps - 1)) * 100;
          setMinLabelStyle({ position: 'absolute', left: `${minPercent}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap' });
        } else { // Caso con un solo timestamp
          setMinLabelStyle({ position: 'absolute', left: '0%', transform: 'translateX(0%)', whiteSpace: 'nowrap' });
        }
      } else {
        setCurrentMinSliderValueText('');
        setMinLabelStyle({});
      }

      // Gestione etichetta Max
      if (maxIndex >= 0 && maxIndex < totalUniqueTimestamps) {
        if (totalUniqueTimestamps > 1 && minIndex !== maxIndex) { // Mostra max solo se diverso da min e ci sono più punti
          setCurrentMaxSliderValueText(formatTimestampForDisplay(uniqueTimestampsForSlider[maxIndex]));
          const maxPercent = (maxIndex / (totalUniqueTimestamps - 1)) * 100;
          setMaxLabelStyle({ position: 'absolute', left: `${maxPercent}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap' });
        } else if (totalUniqueTimestamps === 1 || minIndex === maxIndex) { // Singolo punto o cursori sovrapposti
           setCurrentMaxSliderValueText(''); // Nascondi testo etichetta max
           setMaxLabelStyle({ display: 'none' }); // Nascondi completamente l'etichetta max
        }
      } else {
        setCurrentMaxSliderValueText('');
        setMaxLabelStyle({});
      }
    } else {
      setCurrentMinSliderValueText('');
      setCurrentMaxSliderValueText('');
      setMinLabelStyle({});
      setMaxLabelStyle({});
    }
  }, [sliderValues, uniqueTimestampsForSlider]);

  useEffect(() => {
    const layer = dataLayers.find(l => l.id === selectedLayerId);
    if (layer && layer.sourceSpecification && 'type' in layer.sourceSpecification && layer.sourceSpecification.type === 'geojson' && layer.sourceSpecification.data) {
      const sourceData = layer.sourceSpecification.data;
      if (typeof sourceData === 'object' && sourceData !== null && 'features' in sourceData && Array.isArray(sourceData.features) && sourceData.features.length > 0 && sourceData.features[0].properties) {
        const keys = Object.keys(sourceData.features[0].properties);
        setFieldOptions(keys.map(key => ({ value: key, label: key })));
      } else {
        setFieldOptions([]);
        setSelectedField('');
      }
    } else {
      setFieldOptions([]);
      setSelectedField('');
    }
  }, [selectedLayerId, dataLayers]);

  useEffect(() => {
    const currentLayer = dataLayers.find(l => l.id === selectedLayerId);
    setActualIdFieldUsedForFilter(null);
    let determinedIdField: string | null = null;

    const layerJustChanged = selectedLayerId !== prevSelectedLayerIdRef.current;
    const fieldJustChanged = selectedField !== prevSelectedFieldRef.current;

    if (selectedLayerId && selectedField && currentLayer &&
        currentLayer.sourceSpecification && 'type' in currentLayer.sourceSpecification &&
        currentLayer.sourceSpecification.type === 'geojson' && currentLayer.sourceSpecification.data &&
        typeof currentLayer.sourceSpecification.data === 'object' &&
        currentLayer.sourceSpecification.data !== null && 'features' in currentLayer.sourceSpecification.data &&
        Array.isArray(currentLayer.sourceSpecification.data.features)) {
      
      const features = currentLayer.sourceSpecification.data.features as GeoJSON.Feature[];
      setFeatureCount(features.length);
      // console.log(`[TIME_WIDGET_POPULATE] Inizio elaborazione. Numero totale di feature nel layer: ${features.length}`);

      const tempFeatureIdAndTimestampList: { idValue: string | number, timestamp: number }[] = [];
      const tempTimestamps: number[] = [];
      
      currentLayer.sourceSpecification.data.features.forEach((feature: any) => {
        let featureId: string | number | undefined = undefined;
        let idSourceField: string | null = null;

        if (feature.id !== undefined && feature.id !== null) {
          featureId = feature.id;
          idSourceField = '$id (feature.id)';
        } else if (feature.properties?.fid !== undefined && feature.properties?.fid !== null) {
          featureId = feature.properties.fid;
          idSourceField = 'fid';
        } else if (feature.properties?.OBJECTID !== undefined && feature.properties?.OBJECTID !== null) {
          featureId = feature.properties.OBJECTID;
          idSourceField = 'OBJECTID';
        } else if (feature.properties?.id !== undefined && feature.properties?.id !== null) {
          featureId = feature.properties.id;
          idSourceField = 'id';
        }
        
        const rawValue = feature.properties ? feature.properties[selectedField as string] : undefined;
        let dateObject;
        if (typeof rawValue === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(rawValue)) {
          const parts = rawValue.split('/');
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            dateObject = new Date(Date.UTC(year, month, day));
          } else {
            dateObject = new Date(NaN);
          }
        } else if (rawValue !== undefined) {
          dateObject = new Date(rawValue as string | number | Date);
        } else {
          dateObject = new Date(NaN);
        }
        const timestamp = dateObject.getTime();

        let finalIdValue: number | string | undefined = undefined;
        if (idSourceField) {
            if (idSourceField === 'fid' || idSourceField === 'OBJECTID') {
                if (typeof featureId === 'number') {
                    finalIdValue = featureId;
                } else if (typeof featureId === 'string' && !isNaN(Number(featureId))) {
                    finalIdValue = Number(featureId);
                } else {
                    // console.warn(`[ID_CONV_FAIL] Feature Index ${featureIndex}: ID da '${idSourceField}' ('${featureId}') non convertibile a numero. Feature:`, JSON.stringify(feature).substring(0,500));
                }
            } else {
                if (typeof featureId === 'string' || typeof featureId === 'number') {
                    finalIdValue = featureId;
                } else {
                     // console.warn(`[ID_TYPE_FAIL] Feature Index ${featureIndex}: ID da '${idSourceField}' ('${featureId}') non è stringa o numero. Feature:`, JSON.stringify(feature).substring(0,500));
                }
            }
        }

        if (finalIdValue === undefined) {
             // console.error(`[ID_FINAL_FAIL] Feature Index ${featureIndex}: finalIdValue è undefined. idSourceField: '${idSourceField}', featureId originale: '${featureId}'. Feature saltata per featureIdAndTimestampList. Feature:`, JSON.stringify(feature).substring(0,500));
        }

        if (isNaN(timestamp)) {
            // console.warn(`[DATE_PARSE_FAIL] Feature Index ${featureIndex}: Data non valida per valore "${rawValue}". Feature saltata per featureIdAndTimestampList. Feature:`, JSON.stringify(feature).substring(0,500));
        }

        if (!isNaN(timestamp) && finalIdValue !== undefined) {
            tempFeatureIdAndTimestampList.push({ idValue: finalIdValue, timestamp });
            tempTimestamps.push(timestamp);
        } else {
            // console.warn(`[ADD_TO_LIST_FAIL] Feature Index ${featureIndex}: Non aggiunta a featureIdAndTimestampList. Data Valida: ${!isNaN(timestamp)}, ID Valido: ${finalIdValue !== undefined}. Raw Date: "${rawValue}", Original ID: "${featureId}" from "${idSourceField}"`);
        }
        
        if (idSourceField && !determinedIdField && finalIdValue !== undefined) {
            if (idSourceField === '$id (feature.id)') {
                determinedIdField = '$id';
            } else {
                determinedIdField = idSourceField;
            }
        }
      });
      
      setActualIdFieldUsedForFilter(determinedIdField);
      // console.log(`[ID_FIELD_SET] actualIdFieldUsedForFilter impostato a: ${determinedIdField}`);
      
      // console.log(`[TIME_WIDGET_POPULATE] Fine elaborazione. Feature processate con successo per ID e Data: ${tempFeatureIdAndTimestampList.length}. Timestamp unici raccolti: ${tempTimestamps.length}.`);
      setFeatureIdAndTimestampList(tempFeatureIdAndTimestampList);
      // console.log(`[POPULATE_LISTS] featureIdAndTimestampList (${tempFeatureIdAndTimestampList.length} elementi):`, tempFeatureIdAndTimestampList.length > 0 ? JSON.stringify(tempFeatureIdAndTimestampList.slice(0, 5)) : "Lista vuota");
      const uniqueSortedTimestamps = Array.from(new Set(tempTimestamps)).sort((a, b) => a - b);
      setUniqueTimestampsForSlider(uniqueSortedTimestamps);

      setFeaturesInRangeExceptionCount(null);
      // mapInstance.setFilter è gestito sotto, condizionalmente

      if (uniqueSortedTimestamps.length > 0) {
        const newMin = 0;
        const newMax = uniqueSortedTimestamps.length - 1;
        // setSliderMin(newMin); // Non esiste setSliderMin, min è una prop dello Slider
        // setSliderMax(newMax); // Non esiste setSliderMax, max è una prop dello Slider
        if (layerJustChanged || fieldJustChanged) {
            setSliderValues([newMin, newMax]);
            setLastAppliedFilterString(JSON.stringify(null));
            if (mapInstance && currentLayer?.maplibreLayerId) {
                mapInstance.setFilter(currentLayer.maplibreLayerId, null);
                if (typeof updateLayerProperties === 'function') {
                     updateLayerProperties({ id: currentLayer.id, properties: { filter: null } });
                }
            }
        }
        const newMessage = `Slider aggiornato con ${uniqueSortedTimestamps.length} valori unici.`;
        setSliderUpdateMessage(newMessage);
        setIsExpanded(!!newMessage || (features.length > 0));
      } else {
        if (layerJustChanged || fieldJustChanged) {
            setSliderValues([MIN_SLIDER_VALUE, MIN_SLIDER_VALUE]); // O [0,0] come da istruzioni
            setLastAppliedFilterString(JSON.stringify(null));
            if (mapInstance && currentLayer?.maplibreLayerId) {
                mapInstance.setFilter(currentLayer.maplibreLayerId, null);
                if (typeof updateLayerProperties === 'function') {
                     updateLayerProperties({ id: currentLayer.id, properties: { filter: null } });
                }
            }
        }
        const newMessage = 'Nessun valore data/ora valido trovato nel campo selezionato.';
        setSliderUpdateMessage(newMessage);
        setIsExpanded(!!newMessage || (features.length > 0));
      }
    } else {
      setFeatureCount(null);
      setSliderUpdateMessage('');
      setUniqueTimestampsForSlider([]);
      setFeatureIdAndTimestampList([]);
      setFeaturesInRangeExceptionCount(null);
      if (layerJustChanged || fieldJustChanged) { // Anche qui, resetta solo se layer/campo cambiano
        setSliderValues([MIN_SLIDER_VALUE, MAX_SLIDER_VALUE]); // O [0,0]
        setLastAppliedFilterString(JSON.stringify(null));
        const layerToClear = dataLayers.find(l => l.id === selectedLayerId || l.id === prevSelectedLayerIdRef.current); // Considera anche il layer precedente
        if (mapInstance && layerToClear?.maplibreLayerId) {
            mapInstance.setFilter(layerToClear.maplibreLayerId, null);
            if (typeof updateLayerProperties === 'function') {
                 updateLayerProperties({ id: layerToClear.id, properties: { filter: null } });
            }
        }
      }
      setActualIdFieldUsedForFilter(null);
      // setLastAppliedFilterString(JSON.stringify(null)); // Già gestito sopra condizionalmente
      setIsExpanded(false);
      
      // La logica di pulizia del filtro per layerToClear è già gestita sopra
      // se layerJustChanged o fieldJustChanged sono veri.
      // Se non sono cambiati e siamo in questo blocco else (es. selectedField diventa vuoto),
      // il filtro dovrebbe essere gestito dall'altro useEffect [sliderValues, ...]
    }
    // Aggiorna i ref alla fine dell'effetto
    prevSelectedLayerIdRef.current = selectedLayerId;
    prevSelectedFieldRef.current = selectedField;

  }, [selectedLayerId, selectedField, dataLayers, mapInstance, updateLayerProperties]); // updateLayerProperties aggiunto come dipendenza

  useEffect(() => {
    const currentLayer = dataLayers.find(l => l.id === selectedLayerId);

    if (mapInstance && currentLayer?.maplibreLayerId && selectedField && uniqueTimestampsForSlider.length > 0 && featureIdAndTimestampList.length > 0) {
      let idFilterFieldExpression: string | any[] | null = null;
      if (actualIdFieldUsedForFilter === '$id') { // MODIFICATO
        idFilterFieldExpression = '$id';
      } else if (actualIdFieldUsedForFilter) { // MODIFICATO
        idFilterFieldExpression = ['get', actualIdFieldUsedForFilter]; // MODIFICATO
      }

      // Verifica accesso out-of-bounds
      if (sliderValues[0] >= uniqueTimestampsForSlider.length || sliderValues[1] >= uniqueTimestampsForSlider.length) {
        // console.error('useEffect [sliderValues] - Accesso out-of-bounds per uniqueTimestampsForSlider:', sliderValues, 'len:', uniqueTimestampsForSlider.length);
        if (mapInstance && currentLayer.maplibreLayerId) {
          mapInstance.setFilter(currentLayer.maplibreLayerId, null);
          const newFilterString = JSON.stringify(null);
          if (newFilterString !== lastAppliedFilterString) {
            if (typeof updateLayerProperties === 'function') {
              updateLayerProperties({ id: currentLayer.id, properties: { filter: null } });
              setLastAppliedFilterString(newFilterString);
              // console.log(`[STORE_FILTER_UPDATE] Reset filtro nello store per layer ${currentLayer.id} a null (useEffect sliderValues - out-of-bounds).`);
            }
          } else {
            // console.log(`[STORE_FILTER_UPDATE] Filtro (null) non modificato (out-of-bounds), skip aggiornamento store.`);
          }
        }
        setFeaturesInRangeExceptionCount(0);
        return;
      }

      const actualMinTimestamp = uniqueTimestampsForSlider[sliderValues[0]];
      const actualMaxTimestamp = uniqueTimestampsForSlider[sliderValues[1]];

      const minSliderVal = 0;
      const maxSliderVal = uniqueTimestampsForSlider.length > 0 ? uniqueTimestampsForSlider.length - 1 : 0;

      // Costruzione visibleFeatureIds
      const visibleFeatureIds = featureIdAndTimestampList
        .filter(item => item.timestamp >= actualMinTimestamp && item.timestamp <= actualMaxTimestamp)
        .map(item => item.idValue) // idValue dovrebbe già avere il tipo corretto
        .filter(id => id !== null && id !== undefined); // Assicura che non ci siano null/undefined
      
      // // console.log('[VISIBLE_IDS_CONSTRUCTION] visibleFeatureIds:', JSON.stringify(visibleFeatureIds));
      // // if (visibleFeatureIds.length > 0) {
      // //     // console.log(`[VISIBLE_IDS_CONSTRUCTION] Primo ID in visibleFeatureIds: ${visibleFeatureIds[0]}, Tipo: ${typeof visibleFeatureIds[0]}`);
      // // }

      if (uniqueTimestampsForSlider.length === 0 ||
          (sliderValues[0] === minSliderVal && sliderValues[1] === maxSliderVal && uniqueTimestampsForSlider.length > 0)
      ) {
        // // console.log(`[SET_FILTER_LOGIC] Resetting filter to null for layer ${currentLayer.maplibreLayerId}`);
        mapInstance.setFilter(currentLayer.maplibreLayerId, null);
        const newFilterString = JSON.stringify(null);
        if (newFilterString !== lastAppliedFilterString) {
          if (typeof updateLayerProperties === 'function') {
            updateLayerProperties({ id: currentLayer.id, properties: { filter: null } });
            setLastAppliedFilterString(newFilterString);
            // console.log(`[STORE_FILTER_UPDATE] Aggiornato filtro nello store per layer ${currentLayer.id} a null (slider 100%).`);
          }
        } else {
          // console.log(`[STORE_FILTER_UPDATE] Filtro (null) non modificato (slider 100%), skip aggiornamento store.`);
        }
        setFeaturesInRangeExceptionCount(featureIdAndTimestampList.length); // Conteggio quando slider è 100%
      } else if (idFilterFieldExpression && selectedField) {
          // --------------- INIZIO BLOCCO TEST SETFILTER (ATTIVATO) ---------------
          const performSetFilterTest = false; // Imposta a false per disattivare il test
          if (performSetFilterTest) {
              const testVisibleIds = [4, 5]; // Assumendo che questi ID esistano e siano numeri
              // Assicurati che idFilterFieldExpression sia corretto per il tuo caso di test.
              // Se actualIdFieldUsedForFilter è 'fid' (e 'fid' contiene numeri), idFilterFieldExpression sarà ['get', 'fid']
              // Se actualIdFieldUsedForFilter è '$id', idFilterFieldExpression sarà '$id'
              // Adatta idFilterFieldExpression qui se necessario per il test specifico.
              // Esempio: const testIdFilterFieldExpression = ['get', 'fid']; o const testIdFilterFieldExpression = '$id';
              
              if (idFilterFieldExpression) { // Verifica che idFilterFieldExpression sia definito
                  // const filterExpression = ['in', idFilterFieldExpression, ...testVisibleIds]; // Vecchia logica
                  const filterExpression = ['in', idFilterFieldExpression, ['literal', testVisibleIds]]; // NUOVA logica con ['literal', ...]
                  // console.log(`[SET_FILTER_TEST] Layer ID: ${currentLayer.maplibreLayerId}, Filtro TEST:`, JSON.stringify(filterExpression));
                  try {
                      mapInstance.setFilter(currentLayer.maplibreLayerId, filterExpression as FilterSpecification);
                      setFeaturesInRangeExceptionCount(testVisibleIds.length);
                      // console.log('[SET_FILTER_TEST] Filtro di test applicato con successo.');
                  } catch (e) {
                      // console.error("[SET_FILTER_TEST_ERROR] Errore:", e, "Filtro TEST:", JSON.stringify(filterExpression));
                  }
              } else {
                  // console.warn("[SET_FILTER_TEST] idFilterFieldExpression non definito, impossibile eseguire il test di setFilter.");
              }
          } else {
          // --------------- FINE BLOCCO TEST SETFILTER --------------- (Logica originale sotto)
            if (visibleFeatureIds.length > 0) {
                // console.log(`[FILTER_TYPE_CHECK] Tipo primo ID in visibleFeatureIds: ${typeof visibleFeatureIds[0]}. Campo ID per filtro: ${JSON.stringify(idFilterFieldExpression)}`);
                // const filterExpression = ['in', idFilterFieldExpression, ...visibleFeatureIds]; // Vecchia logica
                const filterExpression = ['in', idFilterFieldExpression, ['literal', visibleFeatureIds]]; // NUOVA logica con ['literal', ...]
                // // console.log(`[SET_FILTER_LOGIC - SPECIFIC] Layer ID: ${currentLayer.maplibreLayerId}, Filtro:`, JSON.stringify(filterExpression));
                try {
                    mapInstance.setFilter(currentLayer.maplibreLayerId, filterExpression as FilterSpecification);
                    const newFilterString = JSON.stringify(filterExpression);
                    if (newFilterString !== lastAppliedFilterString) {
                        if (typeof updateLayerProperties === 'function') {
                            updateLayerProperties({ id: currentLayer.id, properties: { filter: filterExpression as FilterSpecification } });
                            setLastAppliedFilterString(newFilterString);
                            // console.log(`[STORE_FILTER_UPDATE] Aggiornato filtro nello store per layer ${currentLayer.id}:`, newFilterString);
                        }
                    } else {
                        // console.log(`[STORE_FILTER_UPDATE] Filtro non modificato (${newFilterString}), skip aggiornamento store.`);
                    }
                    setFeaturesInRangeExceptionCount(visibleFeatureIds.length); // Conteggio feature nel range
                  } catch (e) {
                    // console.error("[SET_FILTER_ERROR - SPECIFIC] Errore:", e, "Filtro:", JSON.stringify(filterExpression));
                    mapInstance.setFilter(currentLayer.maplibreLayerId, null); // Fallback: mostra tutto
                    const newFallbackFilterString = JSON.stringify(null);
                    if (newFallbackFilterString !== lastAppliedFilterString) {
                        if (typeof updateLayerProperties === 'function') {
                            updateLayerProperties({ id: currentLayer.id, properties: { filter: null } });
                            setLastAppliedFilterString(newFallbackFilterString);
                            // console.log(`[STORE_FILTER_UPDATE] Reset filtro nello store per layer ${currentLayer.id} a null (errore setFilter specific).`);
                        }
                    } else {
                         // console.log(`[STORE_FILTER_UPDATE] Filtro (null) non modificato (errore setFilter specific), skip aggiornamento store.`);
                    }
                    setFeaturesInRangeExceptionCount(featureIdAndTimestampList.length);
                  }
            } else { // Nessuna feature nel range specifico, ma un campo è selezionato
                // const filterToHide = ['in', idFilterFieldExpression, '']; // Vecchia logica
                const filterToHide = ['in', idFilterFieldExpression, ['literal', ['']]]; // NUOVA logica, per coerenza, anche se un array vuoto o con un non-match basterebbe
                // // console.log(`[SET_FILTER_LOGIC - HIDE_ALL_IN_RANGE] Layer ID: ${currentLayer.maplibreLayerId}, Filtro:`, JSON.stringify(filterToHide));
                try {
                    mapInstance.setFilter(currentLayer.maplibreLayerId, filterToHide as FilterSpecification);
                    const newFilterString = JSON.stringify(filterToHide);
                    if (newFilterString !== lastAppliedFilterString) {
                        if (typeof updateLayerProperties === 'function') {
                            updateLayerProperties({ id: currentLayer.id, properties: { filter: filterToHide as FilterSpecification } });
                            setLastAppliedFilterString(newFilterString);
                            // console.log(`[STORE_FILTER_UPDATE] Aggiornato filtro (hide all) nello store per layer ${currentLayer.id}:`, newFilterString);
                        }
                    } else {
                        // console.log(`[STORE_FILTER_UPDATE] Filtro (hide all) non modificato (${newFilterString}), skip aggiornamento store.`);
                    }
                    setFeaturesInRangeExceptionCount(0); // Nessuna feature visibile
                  } catch (e) {
                    // console.error("[SET_FILTER_ERROR - HIDE_ALL_IN_RANGE] Errore:", e, "Filtro:", JSON.stringify(filterToHide));
                    mapInstance.setFilter(currentLayer.maplibreLayerId, null);
                    const newFallbackFilterString = JSON.stringify(null);
                    if (newFallbackFilterString !== lastAppliedFilterString) {
                        if (typeof updateLayerProperties === 'function') {
                            updateLayerProperties({ id: currentLayer.id, properties: { filter: null } });
                            setLastAppliedFilterString(newFallbackFilterString);
                            // console.log(`[STORE_FILTER_UPDATE] Reset filtro nello store per layer ${currentLayer.id} a null (errore setFilter hide all).`);
                        }
                    } else {
                        // console.log(`[STORE_FILTER_UPDATE] Filtro (null) non modificato (errore setFilter hide all), skip aggiornamento store.`);
                    }
                    setFeaturesInRangeExceptionCount(featureIdAndTimestampList.length);
                  }
            }
           } // Chiusura dell'else del blocco di test
      } else {
        const currentFilter = mapInstance.getFilter(currentLayer.maplibreLayerId);
        if (currentFilter === null || currentFilter === undefined) {
            setFeaturesInRangeExceptionCount(featureIdAndTimestampList.length);
        } else {
             if (mapInstance && currentLayer?.maplibreLayerId && !selectedField) {
                 mapInstance.setFilter(currentLayer.maplibreLayerId, null);
                 const newFilterString = JSON.stringify(null);
                 if (newFilterString !== lastAppliedFilterString) {
                   if (typeof updateLayerProperties === 'function') {
                      updateLayerProperties({ id: currentLayer.id, properties: { filter: null } });
                      setLastAppliedFilterString(newFilterString);
                      // console.log(`[STORE_FILTER_UPDATE] Reset filtro nello store per layer ${currentLayer.id} a null (else branch, no selectedField).`);
                   }
                 } else {
                    // console.log(`[STORE_FILTER_UPDATE] Filtro (null) non modificato (no selectedField), skip aggiornamento store.`);
                 }
                 setFeaturesInRangeExceptionCount(featureIdAndTimestampList.length);
             } else if (currentFilter === null || currentFilter === undefined) { // Doppia verifica, ma sicura
                 setFeaturesInRangeExceptionCount(featureIdAndTimestampList.length);
                 // Non c'è un filtro da aggiornare nello store se era già null
             }
        }
      }
    } else {
        // Questo blocco gestisce il caso in cui le condizioni iniziali per il filtraggio non sono soddisfatte
        // (es. mapInstance non disponibile, nessun layer selezionato, nessun campo selezionato, etc.)
        if (mapInstance && currentLayer?.maplibreLayerId) {
            const currentFilter = mapInstance.getFilter(currentLayer.maplibreLayerId);
            if (currentFilter !== null && currentFilter !== undefined) { // Resetta il filtro solo se ce n'è uno attivo
                mapInstance.setFilter(currentLayer.maplibreLayerId, null);
                const newFilterString = JSON.stringify(null);
                if (newFilterString !== lastAppliedFilterString) {
                  if (typeof updateLayerProperties === 'function') {
                    updateLayerProperties({ id: currentLayer.id, properties: { filter: null } });
                    setLastAppliedFilterString(newFilterString);
                    // console.log(`[STORE_FILTER_UPDATE] Reset filtro nello store per layer ${currentLayer.id} a null (else branch, condizioni non soddisfatte).`);
                  }
                } else {
                  // console.log(`[STORE_FILTER_UPDATE] Filtro (null) non modificato (condizioni non soddisfatte), skip aggiornamento store.`);
                }
            }
        }
        // Se non ci sono uniqueTimestampsForSlider, significa che non ci sono date valide o il campo non è stato processato.
        // featureIdAndTimestampList potrebbe contenere elementi se gli ID sono stati estratti ma le date no.
        // In generale, se non possiamo filtrare per data, mostriamo tutte le feature del layer (filtro null).
        setFeaturesInRangeExceptionCount(featureIdAndTimestampList.length);
    }
  }, [sliderValues, uniqueTimestampsForSlider, mapInstance, selectedLayerId, selectedField, dataLayers, featureIdAndTimestampList, actualIdFieldUsedForFilter, updateLayerProperties]); // MODIFICATO


  const handleLayerChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLayerId(event.target.value);
    // Resettare field e slider quando cambia il layer
    setSelectedField('');
    setUniqueTimestampsForSlider([]);
    setFeatureIdAndTimestampList([]);
    setActualIdFieldUsedForFilter(null); // MODIFICATO: Resetta anche actualIdFieldUsedForFilter
    setLastAppliedFilterString(JSON.stringify(null)); // RESET quando cambia il layer
    setSliderValues([MIN_SLIDER_VALUE, MAX_SLIDER_VALUE]);
    setFeaturesInRangeExceptionCount(null);
    setSliderUpdateMessage('');
  };

  const handleFieldChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedField(event.target.value);
    // Quando il campo cambia, il filtro sulla mappa viene resettato a null
    // dall'useEffect che gestisce selectedField.
    // Di conseguenza, anche lastAppliedFilterString dovrebbe essere resettato
    // per riflettere che il filtro "attivamente applicato dal widget" è ora "null".
    // Questo reset avviene nell'useEffect [selectedLayerId, selectedField, dataLayers, mapInstance]
    // quando selectedField cambia e porta al reset del filtro.
    // Oppure, se l'intenzione è che il cambio di campo resetti immediatamente lo stato del filtro applicato:
    // setLastAppliedFilterString(JSON.stringify(null)); // Potrebbe essere ridondante se l'effect lo fa già.
    // L'istruzione dice: "Nell'useEffect di selectedField/selectedLayerId, quando resetti le cose: setLastAppliedFilterString(JSON.stringify(null));"
    // Quindi il reset è gestito nell'useEffect.
  };

  const handleSliderChange = (newValues: number | number[]) => {
    if (Array.isArray(newValues) && newValues.length === 2) {
      setSliderValues(newValues as [number, number]);
    }
  };
  
  if (!isVisible) {
    return null;
  }

  const selectOptions: SelectOption[] = dataLayers
    .filter(layer =>
      layer.sourceSpecification &&
      'type' in layer.sourceSpecification &&
      layer.sourceSpecification.type === 'geojson' &&
      layer.sourceSpecification.data &&
      typeof layer.sourceSpecification.data === 'object' && // Verifica esplicita che data sia un oggetto
      'features' in layer.sourceSpecification.data // E che contenga features
    )
    .map((layer: LayerBase) => ({
    value: layer.id,
    label: layer.name,
  }));

  return (
    <div className={styles.timeWidgetContainer}>
      <div className={styles.controlsRow}>
        <ClockFading size={20} />
        <SelectInput
          options={selectOptions}
          value={selectedLayerId}
          onChange={handleLayerChange}
          className={styles.timeWidgetSelect}
          placeholderOption="Choose Layer"
        />
        <SelectInput
          options={fieldOptions}
          value={selectedField}
          onChange={handleFieldChange}
          className={styles.timeWidgetSelect}
          placeholderOption="Choose Field"
          disabled={fieldOptions.length === 0 || !selectedLayerId}
        />
        <div className={styles.timeSlider}>
          <Slider
            range={{ draggableTrack: true }}
            value={sliderValues}
            onChange={handleSliderChange}
            min={uniqueTimestampsForSlider.length > 0 ? 0 : MIN_SLIDER_VALUE}
            max={uniqueTimestampsForSlider.length > 0 ? uniqueTimestampsForSlider.length - 1 : MAX_SLIDER_VALUE}
            step={1}
            allowCross={false}
            disabled={uniqueTimestampsForSlider.length === 0 || !selectedField}
          />
          {(selectedField && uniqueTimestampsForSlider.length > 0) && (
            <div className={styles.sliderValueLabelsContainer}>
              {currentMinSliderValueText && <span className={styles.sliderTooltip} style={minLabelStyle}>{currentMinSliderValueText}</span>}
              {currentMaxSliderValueText && <span className={styles.sliderTooltip} style={maxLabelStyle}>{currentMaxSliderValueText}</span>}
            </div>
          )}
        </div>
      </div>
      {/* {isExpanded && (
        <div className={styles.feedbackRow}>
          {featureCount !== null && featureCount > 0 && <p className={styles.feedbackText}>Feature nel layer: {featureCount}</p>}
          {sliderUpdateMessage && <p className={styles.feedbackText}>{sliderUpdateMessage}</p>}
          {typeof featuresInRangeExceptionCount === 'number' && (
            <p className={styles.feedbackText}>Feature nel range selezionato: {featuresInRangeExceptionCount}</p>
          )}
        </div>
      )} */}
    </div>
  );
};

export default TimeWidgetContainer;